// src/pages/ClientData/hooks/useClientDataSearch.js
import { useCallback, useMemo, useState } from 'react';
import {
  searchClientsByName,
  getClientData,
  getClientProcesses,
  getProcessDetail,
  getProcessMovements,
  getMovementFiles,
  saveClientDocument,
  transcreverOneDriveFile,
  statusOneDriveTranscricao,
  salvarOneDriveFicheiro
} from '../api';
import { getClientCodeFromProcessCode } from '../utils/masks';
import { buildFullContent } from '../utils/buildContent';

const extractErrorMessage = (error, fallback) =>
  error.response?.data?.message || error.message || fallback;

const INTERVALO_POLLING_MS = 3000;
const MAX_TENTATIVAS_POLLING = 60; // ~3 minutos

// Limite de segurança para a paginação de movimentos no carregamento em massa
const MAX_BULK_PAGES = 200;

const aguardar = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// A API devolve "Nome" como título descritivo (sem extensão) — o nome real do
// ficheiro (com extensão) só está garantido no último segmento do "Caminho"
const obterNomeFicheiroReal = (ficheiro) => {
  const partesCaminho = (ficheiro.Caminho || '').split('/');
  const baseNome = partesCaminho[partesCaminho.length - 1];
  return baseNome && baseNome.includes('.') ? baseNome : ficheiro.Nome;
};

export function useClientDataSearch() {
  const [clientCodeInput, setClientCodeInput] = useState('');
  const [processCodeInput, setProcessCodeInput] = useState('');

  // Pesquisa por nome (Duralex /ai/clients?search=)
  const [nameSearchInput, setNameSearchInput] = useState('');
  const [nameSearching, setNameSearching] = useState(false);
  const [nameSearchError, setNameSearchError] = useState('');
  const [clientMatches, setClientMatches] = useState(null); // null = diálogo fechado

  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const [resolvedClientCode, setResolvedClientCode] = useState('');

  const [clientData, setClientData] = useState(null);
  const [processes, setProcesses] = useState([]);

  const [processDetails, setProcessDetails] = useState({});
  const [processDetailLoading, setProcessDetailLoading] = useState({});
  const [processDetailErrors, setProcessDetailErrors] = useState({});

  const [movements, setMovements] = useState({});
  const [movementsLoading, setMovementsLoading] = useState({});
  const [movementsErrors, setMovementsErrors] = useState({});

  const [movementFiles, setMovementFiles] = useState({});
  const [movementFilesLoading, setMovementFilesLoading] = useState({});
  const [movementFilesErrors, setMovementFilesErrors] = useState({});

  const [saving, setSaving] = useState(false);

  const [fileTranscriptions, setFileTranscriptions] = useState({});
  const [fileTranscriptionLoading, setFileTranscriptionLoading] = useState({});
  const [fileTranscriptionErrors, setFileTranscriptionErrors] = useState({});
  const [fileSaving, setFileSaving] = useState({});

  // Opções de carregamento total (checkboxes antes de "Pesquisar")
  const [loadAllMovements, setLoadAllMovements] = useState(false); // check1
  const [loadAllFiles, setLoadAllFiles] = useState(false);         // check2 (implica movimentos)

  // Estado do diálogo de progresso do carregamento em massa
  const [bulkProgress, setBulkProgress] = useState(null);

  const resetResults = () => {
    setResolvedClientCode('');
    setClientData(null);
    setProcesses([]);
    setProcessDetails({});
    setProcessDetailLoading({});
    setProcessDetailErrors({});
    setMovements({});
    setMovementsLoading({});
    setMovementsErrors({});
    setMovementFiles({});
    setMovementFilesLoading({});
    setMovementFilesErrors({});
    setFileTranscriptions({});
    setFileTranscriptionLoading({});
    setFileTranscriptionErrors({});
    setFileSaving({});
  };

  // Carrega, para cada processo, todos os movimentos (todas as páginas) e,
  // opcionalmente, os ficheiros de cada movimento — montando o contexto completo.
  // Mantém as secções colapsadas mas com os dados já em memória e alimenta o
  // diálogo de progresso a cada etapa do loop de paginação.
  const runBulkLoad = useCallback(async (processesList, { withFiles }) => {
    let movementsLoaded = 0;
    let filesLoaded = 0;

    setBulkProgress({
      phase: 'movimentos',
      withFiles,
      processesTotal: processesList.length,
      processesDone: 0,
      currentProcess: '',
      currentPage: 0,
      lastPage: 0,
      movementsLoaded: 0,
      filesLoaded: 0,
      error: ''
    });

    try {
      for (let i = 0; i < processesList.length; i += 1) {
        const processo = processesList[i];
        const codigoProcesso = processo['Cod. Processo'];

        setBulkProgress((prev) => ({
          ...prev,
          phase: 'movimentos',
          processesDone: i,
          currentProcess: codigoProcesso,
          currentPage: 0,
          lastPage: 0
        }));

        // Detalhe do processo (enriquece o contexto; a secção continua colapsada)
        try {
          const detResp = await getProcessDetail(codigoProcesso);
          if (detResp.data.success) {
            setProcessDetails((prev) => ({ ...prev, [codigoProcesso]: detResp.data.data }));
          }
        } catch (error) {
          // Falha no detalhe não interrompe o carregamento em massa
        }

        // Paginação completa dos movimentos
        let page = 1;
        let lastPage = 1;
        let movimentosProcesso = [];

        do {
          const resp = await getProcessMovements(codigoProcesso, page);
          if (!resp.data.success) break;

          const { data, pagination } = resp.data.data;
          movimentosProcesso = movimentosProcesso.concat(data || []);
          lastPage = pagination?.last_page || 1;
          movementsLoaded += (data || []).length;

          // Congela snapshots por iteração para os updaters não capturarem
          // referências mutáveis do loop (array, page, contadores)
          const movimentosSnapshot = [...movimentosProcesso];
          const pageSnapshot = page;
          const lastPageSnapshot = lastPage;
          const movementsLoadedSnapshot = movementsLoaded;

          setMovements((prev) => ({
            ...prev,
            [codigoProcesso]: { data: movimentosSnapshot, pagination }
          }));

          setBulkProgress((prev) => ({
            ...prev,
            currentPage: pageSnapshot,
            lastPage: lastPageSnapshot,
            movementsLoaded: movementsLoadedSnapshot
          }));

          page += 1;
        } while (page <= lastPage && page <= MAX_BULK_PAGES);

        // Ficheiros de cada movimento (apenas com check2)
        if (withFiles && movimentosProcesso.length) {
          setBulkProgress((prev) => ({ ...prev, phase: 'ficheiros' }));

          for (const movimento of movimentosProcesso) {
            const referencia = movimento['Referência'];
            const chave = `${codigoProcesso}::${referencia}`;

            try {
              const fResp = await getMovementFiles(codigoProcesso, referencia);
              if (fResp.data.success) {
                setMovementFiles((prev) => ({ ...prev, [chave]: fResp.data.data }));
                filesLoaded += fResp.data.data?.Ficheiros?.length || 0;
                const filesLoadedSnapshot = filesLoaded;
                setBulkProgress((prev) => ({ ...prev, filesLoaded: filesLoadedSnapshot }));
              }
            } catch (error) {
              // Falha nos ficheiros de um movimento não interrompe o processamento
            }
          }
        }
      }

      setBulkProgress((prev) => ({
        ...prev,
        phase: 'concluido',
        processesDone: processesList.length,
        currentProcess: ''
      }));
    } catch (error) {
      setBulkProgress((prev) => ({
        ...(prev || {}),
        phase: 'erro',
        error: extractErrorMessage(error, 'Erro ao carregar dados em massa')
      }));
    }
  }, []);

  const closeBulkProgress = useCallback(() => setBulkProgress(null), []);

  // overrideClientCode é usado pela pesquisa por nome, que já conhece o código
  // e não pode esperar pelo re-render do input. O botão "Pesquisar" passa o
  // evento de clique como argumento — por isso só strings são aceites.
  const search = useCallback(async (overrideClientCode) => {
    const codigoForcado = typeof overrideClientCode === 'string' ? overrideClientCode.trim() : '';
    const clientCodeValida = codigoForcado || clientCodeInput.trim();
    const processCodeValido = codigoForcado ? '' : processCodeInput.trim();

    if (!clientCodeValida && !processCodeValido) {
      setSearchError('Indique o código do cliente ou o código do processo');
      return;
    }

    const codigoCliente = clientCodeValida
      ? clientCodeValida.padStart(5, '0')
      : getClientCodeFromProcessCode(processCodeValido);

    setSearching(true);
    setSearchError('');
    setHasSearched(false);
    setBulkProgress(null);
    resetResults();

    try {
      const [clientResult, processesResult] = await Promise.allSettled([
        getClientData(codigoCliente),
        getClientProcesses(codigoCliente)
      ]);

      let algumSucesso = false;
      let processosCarregados = [];

      if (clientResult.status === 'fulfilled' && clientResult.value.data.success) {
        setClientData(clientResult.value.data.data);
        algumSucesso = true;
      }

      if (processesResult.status === 'fulfilled' && processesResult.value.data.success) {
        processosCarregados = processesResult.value.data.data || [];
        setProcesses(processosCarregados);
        algumSucesso = true;
      }

      if (!algumSucesso) {
        const erro = clientResult.reason || processesResult.reason;
        setSearchError(extractErrorMessage(erro || {}, 'Não foram encontrados dados para o código indicado'));
      } else {
        setResolvedClientCode(codigoCliente);

        // Carregamento total opcional — check2 (ficheiros) implica movimentos
        const withMovements = loadAllMovements || loadAllFiles;
        if (withMovements && processosCarregados.length) {
          await runBulkLoad(processosCarregados, { withFiles: loadAllFiles });
        }
      }
    } catch (error) {
      setSearchError(extractErrorMessage(error, 'Erro ao pesquisar dados'));
    } finally {
      setSearching(false);
      setHasSearched(true);
    }
  }, [clientCodeInput, processCodeInput, loadAllMovements, loadAllFiles, runBulkLoad]);

  // Preenche o código do cliente escolhido e dispara a pesquisa completa
  const selectClientMatch = useCallback(async (cliente) => {
    const codigo = String(cliente['Cod. Cliente'] || '').replace(/\D/g, '').padStart(5, '0');
    if (!codigo) return;

    setClientMatches(null);
    setNameSearchError('');
    setClientCodeInput(codigo);
    setProcessCodeInput('');
    await search(codigo);
  }, [search]);

  const closeClientMatches = useCallback(() => setClientMatches(null), []);

  const searchByName = useCallback(async () => {
    const nome = nameSearchInput.trim();

    if (!nome) {
      setNameSearchError('Indique o nome do cliente a pesquisar');
      return;
    }

    setNameSearching(true);
    setNameSearchError('');
    setClientMatches(null);

    let encontrados = [];

    try {
      const response = await searchClientsByName(nome);
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erro ao pesquisar clientes por nome');
      }
      encontrados = Array.isArray(response.data.data) ? response.data.data : [];
    } catch (error) {
      setNameSearchError(extractErrorMessage(error, 'Erro ao pesquisar clientes por nome'));
      return;
    } finally {
      setNameSearching(false);
    }

    if (!encontrados.length) {
      setNameSearchError('Não foram encontrados clientes com esse nome');
      return;
    }

    if (encontrados.length === 1) {
      await selectClientMatch(encontrados[0]);
      return;
    }

    setClientMatches(encontrados);
  }, [nameSearchInput, selectClientMatch]);

  const loadProcessDetail = useCallback(async (processCode) => {
    setProcessDetailLoading((prev) => ({ ...prev, [processCode]: true }));
    setProcessDetailErrors((prev) => ({ ...prev, [processCode]: '' }));

    try {
      const response = await getProcessDetail(processCode);
      if (response.data.success) {
        setProcessDetails((prev) => ({ ...prev, [processCode]: response.data.data }));
      }
    } catch (error) {
      setProcessDetailErrors((prev) => ({
        ...prev,
        [processCode]: extractErrorMessage(error, 'Erro ao carregar detalhes do processo')
      }));
    } finally {
      setProcessDetailLoading((prev) => ({ ...prev, [processCode]: false }));
    }
  }, []);

  const loadMovements = useCallback(async (processCode, page = 1) => {
    setMovementsLoading((prev) => ({ ...prev, [processCode]: true }));
    setMovementsErrors((prev) => ({ ...prev, [processCode]: '' }));

    try {
      const response = await getProcessMovements(processCode, page);
      if (response.data.success) {
        const { data, pagination } = response.data.data;
        setMovements((prev) => {
          const anterior = prev[processCode];
          const novosMovimentos = page > 1 && anterior?.data
            ? [...anterior.data, ...(data || [])]
            : (data || []);
          return { ...prev, [processCode]: { data: novosMovimentos, pagination } };
        });
      }
    } catch (error) {
      setMovementsErrors((prev) => ({
        ...prev,
        [processCode]: extractErrorMessage(error, 'Erro ao carregar movimentos do processo')
      }));
    } finally {
      setMovementsLoading((prev) => ({ ...prev, [processCode]: false }));
    }
  }, []);

  const loadMovementFiles = useCallback(async (processCode, referencia) => {
    const chave = `${processCode}::${referencia}`;
    setMovementFilesLoading((prev) => ({ ...prev, [chave]: true }));
    setMovementFilesErrors((prev) => ({ ...prev, [chave]: '' }));

    try {
      const response = await getMovementFiles(processCode, referencia);
      if (response.data.success) {
        setMovementFiles((prev) => ({ ...prev, [chave]: response.data.data }));
      }
    } catch (error) {
      setMovementFilesErrors((prev) => ({
        ...prev,
        [chave]: extractErrorMessage(error, 'Erro ao carregar ficheiros do movimento')
      }));
    } finally {
      setMovementFilesLoading((prev) => ({ ...prev, [chave]: false }));
    }
  }, []);

  const setFileTranscriptionText = useCallback((chave, texto) => {
    setFileTranscriptions((prev) => ({ ...prev, [chave]: texto }));
  }, []);

  const transcreverFicheiro = useCallback(async (processCode, referencia, ficheiro) => {
    const chave = `${processCode}::${referencia}::${ficheiro.ID}`;

    setFileTranscriptionLoading((prev) => ({ ...prev, [chave]: true }));
    setFileTranscriptionErrors((prev) => ({ ...prev, [chave]: '' }));

    try {
      const nomeReal = obterNomeFicheiroReal(ficheiro);
      const response = await transcreverOneDriveFile(ficheiro.Caminho, nomeReal);
      const resultado = response.data;

      if (!resultado.success) {
        throw new Error(resultado.message || 'Erro ao transcrever ficheiro');
      }

      if (!resultado.async) {
        setFileTranscriptions((prev) => ({ ...prev, [chave]: resultado.texto }));
        return;
      }

      // Ficheiro digitalizado — fazer polling ao job assíncrono do Document AI
      let tentativas = 0;
      let concluido = false;

      while (tentativas < MAX_TENTATIVAS_POLLING && !concluido) {
        await aguardar(INTERVALO_POLLING_MS);
        tentativas += 1;

        const statusResponse = await statusOneDriveTranscricao(resultado.jobId, resultado.operationName);
        const statusData = statusResponse.data;

        if (!statusData.success) {
          throw new Error(statusData.message || 'Erro ao transcrever ficheiro digitalizado');
        }

        if (statusData.done) {
          setFileTranscriptions((prev) => ({ ...prev, [chave]: statusData.texto }));
          concluido = true;
        }
      }

      if (!concluido) {
        throw new Error('A transcrição está a demorar demasiado tempo. Tente novamente mais tarde.');
      }
    } catch (error) {
      setFileTranscriptionErrors((prev) => ({
        ...prev,
        [chave]: extractErrorMessage(error, 'Erro ao transcrever ficheiro')
      }));
    } finally {
      setFileTranscriptionLoading((prev) => ({ ...prev, [chave]: false }));
    }
  }, []);

  const salvarFicheiroComoDocumento = useCallback(async (processCode, referencia, ficheiro) => {
    const chave = `${processCode}::${referencia}::${ficheiro.ID}`;
    const codigoCliente = getClientCodeFromProcessCode(processCode) || resolvedClientCode;

    setFileSaving((prev) => ({ ...prev, [chave]: true }));
    const nomeReal = obterNomeFicheiroReal(ficheiro);

    try {
      let texto = fileTranscriptions[chave];

      if (!texto || !texto.trim()) {
        const response = await transcreverOneDriveFile(ficheiro.Caminho, nomeReal);
        const resultado = response.data;

        if (!resultado.success) {
          throw new Error(resultado.message || 'Erro ao transcrever ficheiro');
        }

        if (!resultado.async) {
          texto = resultado.texto;
        } else {
          let tentativas = 0;
          let concluido = false;

          while (tentativas < MAX_TENTATIVAS_POLLING && !concluido) {
            await aguardar(INTERVALO_POLLING_MS);
            tentativas += 1;

            const statusResponse = await statusOneDriveTranscricao(resultado.jobId, resultado.operationName);
            const statusData = statusResponse.data;

            if (!statusData.success) {
              throw new Error(statusData.message || 'Erro ao transcrever ficheiro digitalizado');
            }

            if (statusData.done) {
              texto = statusData.texto;
              concluido = true;
            }
          }

          if (!concluido) {
            throw new Error('A transcrição está a demorar demasiado tempo. Tente novamente mais tarde.');
          }
        }

        setFileTranscriptions((prev) => ({ ...prev, [chave]: texto }));
      }

      const response = await salvarOneDriveFicheiro(nomeReal, texto, codigoCliente);
      if (!response.data.success) {
        return { success: false, message: response.data.message };
      }

      return { success: true };
    } catch (error) {
      return { success: false, message: extractErrorMessage(error, 'Erro ao guardar ficheiro') };
    } finally {
      setFileSaving((prev) => ({ ...prev, [chave]: false }));
    }
  }, [fileTranscriptions, resolvedClientCode]);

  const hasResults = Boolean(clientData || processes.length > 0);

  const fullContent = useMemo(() => buildFullContent({
    clientCode: resolvedClientCode,
    clientData,
    processes,
    processDetails,
    movements,
    movementFiles,
    fileTranscriptions
  }), [resolvedClientCode, clientData, processes, processDetails, movements, movementFiles, fileTranscriptions]);

  const saveToMeusDocumentos = useCallback(async () => {
    if (!resolvedClientCode || !fullContent.trim()) return { success: false, message: 'Não há dados para guardar' };

    setSaving(true);
    try {
      const response = await saveClientDocument(resolvedClientCode, fullContent);
      if (response.data.success) {
        return { success: true };
      }
      return { success: false, message: response.data.message };
    } catch (error) {
      return { success: false, message: extractErrorMessage(error, 'Erro ao guardar documento') };
    } finally {
      setSaving(false);
    }
  }, [resolvedClientCode, fullContent]);

  return {
    clientCodeInput,
    setClientCodeInput,
    processCodeInput,
    setProcessCodeInput,
    searching,
    searchError,
    hasSearched,
    hasResults,
    resolvedClientCode,
    search,

    nameSearchInput,
    setNameSearchInput,
    nameSearching,
    nameSearchError,
    searchByName,
    clientMatches,
    selectClientMatch,
    closeClientMatches,

    clientData,

    processes,
    processDetails,
    processDetailLoading,
    processDetailErrors,
    loadProcessDetail,

    movements,
    movementsLoading,
    movementsErrors,
    loadMovements,

    movementFiles,
    movementFilesLoading,
    movementFilesErrors,
    loadMovementFiles,

    fileTranscriptions,
    fileTranscriptionLoading,
    fileTranscriptionErrors,
    fileSaving,
    setFileTranscriptionText,
    transcreverFicheiro,
    salvarFicheiroComoDocumento,

    loadAllMovements,
    setLoadAllMovements,
    loadAllFiles,
    setLoadAllFiles,
    bulkProgress,
    closeBulkProgress,

    fullContent,
    saving,
    saveToMeusDocumentos
  };
}
